/**
 * 存貨紀錄表單(新增 / 編輯共用)。
 */
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  CreateInventoryRecordSchema,
  type CreateInventoryRecordInput,
  type InventoryRecordResponse,
} from '@app/shared';

export interface InventoryFormProps {
  defaultValues?: Partial<InventoryRecordResponse>;
  submitting?: boolean;
  onSubmit: (values: CreateInventoryRecordInput) => void;
  onCancel?: () => void;
  submitLabel?: string;
  /** 對外通知表單欄位是否有變更(供 modal 判斷關閉提示)。 */
  onDirtyChange?: (dirty: boolean) => void;
}

function today(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function InventoryForm({
  defaultValues,
  submitting,
  onSubmit,
  onCancel,
  submitLabel = '儲存',
  onDirtyChange,
}: InventoryFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CreateInventoryRecordInput>({
    resolver: zodResolver(CreateInventoryRecordSchema),
    defaultValues: {
      itemName: defaultValues?.itemName ?? '',
      recordedAt: defaultValues?.recordedAt ?? today(),
      quantity: defaultValues?.quantity ?? '',
      unit: defaultValues?.unit ?? null,
      notes: defaultValues?.notes ?? null,
    },
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const errorList = Object.entries(errors)
    .map(([k, v]) => [k, (v as { message?: string } | undefined)?.message])
    .filter((x): x is [string, string] => typeof x[1] === 'string');

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="grid gap-3 bg-white border rounded-md p-3"
      data-testid="inventory-form"
    >
      {errorList.length > 0 ? (
        <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
          {errorList.map(([k, m]) => (
            <div key={k}>
              {k}: {m}
            </div>
          ))}
        </div>
      ) : null}

      <label className="grid gap-1">
        <span className="text-sm text-slate-700">品項名稱 *</span>
        <input
          type="text"
          {...register('itemName')}
          className={inputCls}
          data-testid="inv-item-name"
        />
      </label>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
        <label className="grid gap-1">
          <span className="text-sm text-slate-700">入庫日期 *</span>
          <input
            type="date"
            {...register('recordedAt')}
            className={inputCls}
            data-testid="inv-recorded-at"
          />
        </label>

        <label className="grid gap-1">
          <span className="text-sm text-slate-700">數量 *</span>
          <input
            type="text"
            inputMode="decimal"
            {...register('quantity')}
            className={inputCls}
            placeholder="例 5 或 5.25"
            data-testid="inv-quantity"
          />
        </label>

        <label className="grid gap-1">
          <span className="text-sm text-slate-700">單位</span>
          <input
            type="text"
            {...register('unit', { setValueAs: (v) => (v === '' || v == null ? null : v) })}
            className={inputCls}
            placeholder="捲 / 件 / 公尺…"
            data-testid="inv-unit"
          />
        </label>
      </div>

      <label className="grid gap-1">
        <span className="text-sm text-slate-700">備註</span>
        <textarea
          {...register('notes', { setValueAs: (v) => (v === '' || v == null ? null : v) })}
          className={inputCls}
          rows={3}
          data-testid="inv-notes"
        />
      </label>

      <div className="flex gap-2 justify-center">
        <button
          type="submit"
          disabled={submitting}
          className="bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
          data-testid="inv-submit"
        >
          {submitting ? '儲存中…' : submitLabel}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="border rounded px-3 py-1.5 hover:bg-slate-100"
          >
            取消
          </button>
        ) : null}
      </div>
    </form>
  );
}

const inputCls =
  'w-full border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
